import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import UploadedFileIcon from "../../../img/uploaded-file-icon.png";
import { REQUEST_OTP_NS } from "./i18n";

function DragDropFileUpload(props: {
  onFileUpload: (file: File | null) => void;
}) {
  const { onFileUpload } = props;
  const { palette } = useTheme();
  const { t } = useTranslation(REQUEST_OTP_NS);
  
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const registerFileUpload = useCallback((file: File | undefined | null) => {
    if (file) {
      setUploadedFile(file);
      onFileUpload(file);
    }
  }, [setUploadedFile, onFileUpload]);

  const handleDragOver = useCallback((event: React.DragEvent<Element>) => {
    event.preventDefault();
    setDragOver(true);
  }, [setDragOver]);

  const handleDragLeave = useCallback((event: React.DragEvent<Element>) => {
    event.preventDefault();
    setDragOver(false);
  }, [setDragOver]);

  const handleDrop = useCallback((event: React.DragEvent<Element>) => {
    event.preventDefault();

    setDragOver(false);
    registerFileUpload(event.dataTransfer?.files[0]);
  }, [setDragOver, registerFileUpload]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    registerFileUpload(event.target.files?.[0])
  },  [registerFileUpload]);

  const noFileContent = (
    <>
      <FileUploadOutlinedIcon
        sx={{
          fontSize: 20,
          transition: "0.2s",
          color: dragOver ? "#3AA4E6" : palette.text.placeholder,
          mb: 1,
        }}
      />
      <Typography
        sx={{
          transition: "0.2s",
          color: dragOver ? "#3AA4E6" : palette.text.placeholder,
        }}
      >
        {t("dropFileForm.uploadFile.dropFile")}
      </Typography>
    </>
  );

  const fileAddedContent = (
    <>
      <img src={UploadedFileIcon} alt="uploaded_file" />
      <Typography sx={{ mb: 1, mt: 2 }}>
        {uploadedFile?.name ?? null}
      </Typography>
      <Typography
        sx={{
          width: "fit-content",
          cursor: "pointer",
          color: "#1D82C0",
        }}
      >
        {t("dropFileForm.uploadFile.replace")}
      </Typography>
    </>
  );

  const uploadBlockBorderStyle = dragOver ? "#3AA4E6" : "transparent";

  return (
    <Paper
      variant="outlined"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        transition: "0.2s",
        border: uploadedFile ? "none" : "1px dashed",
        borderColor: uploadedFile ? "none" : uploadBlockBorderStyle,
        backgroundColor: dragOver
          ? "rgba(29, 130, 192, 0.12)"
          : palette.divider,
      }}
    >
      <input
        accept="image/*"
        style={{ display: "none" }}
        id="raised-button-file"
        multiple
        type="file"
        onChange={handleChange}
      />
      <label htmlFor="raised-button-file">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            cursor: uploadedFile ? "auto" : "pointer",
            p: uploadedFile ? 2 : 5,
          }}
        >
          {uploadedFile ? fileAddedContent : noFileContent}
        </Box>
      </label>
    </Paper>
  );
}

export default DragDropFileUpload;
